<template>
    <div class="box box-primary">
        <div class="box-header with-border">
            <h3 class="box-title">
                {{ $t('filter') }}
            </h3>

            <div class="box-tools pull-right">
                <button
                    class="btn btn-box-tool"
                    :title="$t('collapse')"
                    type="button"
                    @click="isCollapse = !isCollapse"
                >
                    <i
                        class="fa"
                        :class="{
                            'fa-plus': isCollapse,
                            'fa-minus': !isCollapse,
                        }"
                    />
                </button>
            </div>
        </div>

        <vue-slide-up-down :active="!isCollapse">
            <div class="box-body">
                <div class="box-body-tab">
                    <DateRangeComponent
                        :id="getDynamicId('date')"
                        ref="dateRef"
                        :disabled="isLoading"
                        :end="getExternalParam('end')"
                        :max-span="maxSpan"
                        :start="getExternalParam('start')"
                        class-input="form-control"
                        class-main-block="form-group col-lg-4 col-md-6 col-xs-12"
                        placeholder="Dates Range"
                        title="Dates"
                        @change="changeDate"
                    />
                    <multi-select-component
                        id="clickerFilter"
                        ref="clickerFilterRef"
                        :searchable="false"
                        :disabled="isLoading"
                        :items="itemsClickFilter"
                        :selected="getExternalParam('clickerFilter')"
                        class-main-block="form-group col-lg-4 col-md-6 col-xs-12"
                        title="Clicker Filter"
                        @change="changeClickerFilter"
                    />
                    <select-component
                        id="withoutClickServer"
                        ref="withoutClickServerRef"
                        :searchable="false"
                        :disabled="isLoading"
                        :items="itemsTripleConditionWithoutClick"
                        :selected="getExternalParam('withoutClickServer', 'none')"
                        class-main-block="form-group col-lg-4 col-md-6 col-xs-12"
                        title="Without Click Server"
                        @change="changeWithoutClickServer"
                    />
                    <select-component
                        id="groupBy"
                        ref="groupByRef"
                        :searchable="true"
                        :disabled="isLoading"
                        :items="itemsGroupBy"
                        :selected="getExternalParam('groupBy')"
                        placeholder="Select group by"
                        class-main-block="form-group col-lg-4 col-md-6 col-xs-12"
                        title="Group by"
                        @change="changeGroupBy"
                    />
                    <multi-select-component
                        v-if="existGroupBy"
                        id="filter"
                        ref="filterRef"
                        :searchable="true"
                        :disabled="isLoading"
                        :items="itemsFilter"
                        :selected="getExternalParam('filter')"
                        placeholder="Select or leave empty"
                        class-main-block="form-group col-lg-4 col-md-6 col-xs-12"
                        title="Filters"
                        @change="changeFilter"
                    />
                    <template v-for="value in itemsFilterSelected.filter((item) => item.isMultiselectExtended)">
                        <multi-select-extended-component
                            v-if="existFilter"
                            :id="value.id"
                            :key="value.id"
                            ref="formItemsDynamicRef"
                            :class-main-block="value.classMainBlock"
                            :class-select="value.classSelect"
                            :disabled="isLoading || disabledDynamicFilter"
                            :items="getItems(value.id)"
                            :route="value.route"
                            :selected="getExternalParam(value.id)"
                            :title="value.name"
                            placeholder="Select or leave empty"
                            @change="callMethod(value.change)"
                            @setItems="setItems"
                        />
                    </template>

                    <template v-for="value in itemsFilterSelected.filter((item) => !item.isMultiselectExtended)">
                        <multi-select-component
                            v-if="existFilter"
                            :id="value.id"
                            :key="value.id"
                            ref="formItemsDynamicRef"
                            :class-main-block="value.classMainBlock"
                            :class-select="value.classSelect"
                            :disabled="isLoading || disabledDynamicFilter"
                            :items="getItems(value.id)"
                            :route="value.route"
                            :selected="getExternalParam(value.id)"
                            :title="value.name"
                            placeholder="Select or leave empty"
                            @change="callMethod(value.change)"
                            @setItems="setItems"
                        />
                    </template>
                    <checkbox-component
                        v-if="itemsFilterSelected.length > 0"
                        id="onlyActive"
                        ref="onlyActiveRef"
                        :selected="onlyActive"
                        :disabled="isLoading"
                        class-main-block="form-group col-lg-2 col-md-6 col-xs-12"
                        title="Only Active"
                        @change="changeOnlyActive"
                    />
                    <div class="form-group col-xs-12">
                        <label class="col-xs-12 hidden-xs hidden-lg"></label>
                        <button
                            :disabled="!existGroupBy || isLoading"
                            class="btn btn-sm btn-primary"
                            type="button"
                            @click="getReport()"
                        >
                            Apply
                        </button>
                        <button
                            :disabled="isLoading"
                            class="btn btn-sm btn-info"
                            type="button"
                            @click="clearFilter()"
                        >
                            Clear
                        </button>
                        <div class="delimiter-button"></div>
                        <button
                            :disabled="!existGroupBy || isLoading"
                            class="btn btn-sm bg-yellow"
                            type="button"
                            @click="getReport(true)"
                        >
                            Export
                        </button>
                    </div>
                </div>
            </div>
        </vue-slide-up-down>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import formatsMixin from '@/mixins/formatsMixin';
import DateRangeComponent from '@/components/partials/DateRangeComponent';
import {isArray} from "lodash";

export default {
    name: 'DashboardFilters',
    components: {
        DateRangeComponent,
    },
    mixins: [formatsMixin],
    props: {
        idTab: {
            type: String,
            default: '',
        },
        count: {
            type: Number,
            default: 0,
        },
        dataItems: {
            type: Array,
            default: () => [],
        },
        disabledDynamicFilter: {
            type: Boolean,
            default: false,
        },
        externalParams: {
            type: Object,
            default: () => ({}),
        },
        collapseChart: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isCollapse: false,
            isLoading: false,
            isChartsLoading: false,
            isAdditionalFieldsHidden: false,
            onlyActive: this.getExternalParam('onlyActive', true),
            onlyWithMessages: true,
            report: {},
            existGroupBy: false,
            existFilter: false,
            typeReport: '',
            maxSpan: 1826,
            additionalRelationTitles: [],
            itemsFilterSelected: [],
            itemsGroupBy: [
                { id: 'api', name: 'API' },
                { id: 'provider', name: 'API Provider' },
                { id: 'carrier', name: 'Carrier' },
                { id: 'contactsList', name: 'Contacts List' },
                { id: 'contactsListTag', name: 'Contacts List Tag' },
                { id: 'contactsListCategory', name: 'Contacts List Category' },
                { id: 'predefinedBody', name: 'Predefined Body' },
                { id: 'campaignBody', name: 'Campaign Body' },
                { id: 'gender', name: 'Gender' },
                { id: 'offer', name: 'Offer' },
                { id: 'offerTag', name: 'Offer Tag' },
                { id: 'tld', name: 'Short Domain TLD' },
                { id: 'timezone', name: 'Timezone' },
                { id: 'dlc', name: 'DLC' },
                { id: 'label', name: 'Label' },
                { id: 'state', name: 'State' },
                { id: 'os', name: 'OS' },
                { id: 'batchList', name: 'Batch List' },
            ],
            convertToNumber: {
                clicks_count: true,
                clicks_percent: true,
                cost: true,
                ecpm: true,
                epc: true,
                messages_count: true,
                messages_sent: true,
                messages_undelivered: true,
                messages_failed: true,
                messages_blacklisted: true,
                messages_unsubscribed: true,
                messages_2way_replied: true,
                pcpm: true,
                revenue_amount: true,
                revenue_count: true,
                profit_amount: true,
            },
            formItems: [
                { id: 'date', type: 'static' },
                { id: 'clickerFilter', type: 'static' },
                { id: 'withoutClickServer', type: 'static' },
                { id: 'groupBy', type: 'static' },
                { id: 'filter', type: 'static' },
                { id: 'onlyActive', type: 'static' },
                { id: 'api', type: 'dynamic' },
                { id: 'dlc', type: 'dynamic' },
                { id: 'campaign', type: 'dynamic' },
                { id: 'carrier', type: 'dynamic' },
                { id: 'contactsList', type: 'dynamic' },
                { id: 'contactsListTag', type: 'dynamic' },
                { id: 'contactsListCategory', type: 'dynamic' },
                { id: 'predefinedBody', type: 'dynamic' },
                { id: 'campaignBody', type: 'dynamic' },
                { id: 'offer', type: 'dynamic' },
                { id: 'offerTag', type: 'dynamic' },
                { id: 'provider', type: 'dynamic' },
                { id: 'timezone', type: 'dynamic' },
                { id: 'tld', type: 'dynamic' },
                { id: 'gender', type: 'dynamic' },
                { id: 'label', type: 'dynamic' },
                { id: 'state', type: 'dynamic' },
                { id: 'os', type: 'dynamic' },
                { id: 'batchList', type: 'dynamic' },
            ],
            itemsFilter: [
                {
                    name: 'APIs',
                    id: 'api',
                    route: '/dashboard-advanced/get-filter-list?entity=api',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'API Providers',
                    id: 'provider',
                    route: '/dashboard-advanced/get-filter-list?entity=provider',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterProvider',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Carriers',
                    id: 'carrier',
                    route: '/dashboard-advanced/get-filter-list?entity=carrier',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Contacts Lists',
                    id: 'contactsList',
                    route: '/dashboard-advanced/get-filter-list?entity=contactsList',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: true,
                },
                {
                    name: 'Contacts Lists Tag',
                    id: 'contactsListTag',
                    route: '/dashboard-advanced/get-filter-list?entity=contactsListTag',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Contacts List Category',
                    id: 'contactsListCategory',
                    route: '/dashboard-advanced/get-filter-list?entity=contactsListCategory',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Predefined Body',
                    id: 'predefinedBody',
                    route: '/dashboard-advanced/get-filter-list?entity=predefinedBody',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Campaign Body',
                    id: 'campaignBody',
                    route: '/dashboard-advanced/get-filter-list?entity=campaignBody',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Gender',
                    id: 'gender',
                    route: '/dashboard-advanced/get-filter-list?entity=gender',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Offers',
                    id: 'offer',
                    route: '/dashboard-advanced/get-filter-list?entity=offer',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Offer Tags',
                    id: 'offerTag',
                    route: '/dashboard-advanced/get-filter-list?entity=offerTag',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Timezone',
                    id: 'timezone',
                    route: '/dashboard-advanced/get-filter-list?entity=timezone',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'DLC',
                    id: 'dlc',
                    route: '/dashboard-advanced/get-filter-list?entity=dlc',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Label',
                    id: 'label',
                    route: '/dashboard-advanced/get-filter-list?entity=label',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'State',
                    id: 'state',
                    route: '/dashboard-advanced/get-filter-list?entity=state',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'OS',
                    id: 'os',
                    route: '/dashboard-advanced/get-filter-list?entity=os',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
                {
                    name: 'Batch List',
                    id: 'batchList',
                    route: '/dashboard-advanced/get-filter-list?entity=batchList',
                    classMainBlock: 'form-group col-lg-4 col-md-6 col-xs-12',
                    classSelect: 'form-control',
                    change: 'changeFilterDefault',
                    isMultiselectExtended: false,
                },
            ],
            itemsClickFilter: [
                { id: 1, name: 'None clickers' },
                { id: 2, name: 'All clickers' },
                { id: 3, name: 'Yesterday' },
                { id: 4, name: 'Today' },
                { id: 5, name: 'Last week' },
                { id: 6, name: 'Last month' },
                { id: 7, name: 'Last 2 month' },
                { id: 8, name: 'Initial' },
                { id: 9, name: 'Non-initial' },
            ],
            itemsTripleConditionWithoutClick: [
                { id: 'none', name: 'None' },
                { id: '1', name: 'Yes' },
                { id: '0', name: 'No' },
            ],
        };
    },
    mounted() {
        this.checkDynamicFilters();
        this.changeFilterParameter();
    },
    methods: {
        filterByMessages() {
            this.onlyWithMessages = !this.onlyWithMessages;
        },
        getExternalParam(key, defaultParam) {
            return this.externalParams && this.externalParams[key] !== undefined
                ? this.externalParams[key]
                : defaultParam
                ? defaultParam
                : null;
        },
        getReport(exportReport, byDates = false) {
            const unnecessarySendKeys = ['filter', 'onlyActive'];
            let mainFilter = '';

            this.formItems.forEach((value) => {
                if (unnecessarySendKeys.some((el) => el === value.id)) return;

                const currentRef = this.getCurrentRef(value.id);

                if (currentRef && currentRef.getValue) {
                    let valueRef = currentRef.getValue();
                    valueRef = this.changeEmptyObject(valueRef);

                    if (valueRef && valueRef !== 'none') {
                        mainFilter += mainFilter.length === 0 ? '?' : '&';
                        mainFilter += this.addGetParam(value.id, valueRef);
                    }
                }
            });

            exportReport ? this.eventExportReport(mainFilter) : this.eventGetReport(mainFilter, byDates);
        },
        changeEmptyObject(value) {
            return typeof value === 'object' && value !== null && Object.keys(value).length === 0 ? null : value;
        },
        eventExportReport(mainFilter) {
            let link = document.createElement('a');
            document.body.appendChild(link);
            link.href = this.getBaseUrl() + '/export' + mainFilter;
            link.click();
            link.remove();
        },
        getBaseUrl() {
            return document.location.origin + (new URL(document.location.href)).pathname;
        },
        // Get data by filter
        async eventGetReport(mainFilter, byDates = false) {
            if (byDates) {
                this.isChartsLoading = true;
                this.$emit('setChartsLoading', this.isChartsLoading);
            } else {
                this.collapseChart();

                this.isLoading = true;
                this.$emit('setLoading', this.isLoading);
            }
            EventBus.$emit('alertOptions', { displayAlert: false });

            try {
                const {
                    data: { result: report = {} },
                } = await axios.get(this.getBaseUrl() + '/get' + (byDates ? '/date' : '') + mainFilter);

                this.typeReport = this.itemsGroupBy.find((value) => value.id === this.$refs.groupByRef.getValue()).name;

                if (report.items) {
                    report.items = Object.values(report.items);
                }

                if (report.items && report.totals) {
                    // Additional relations
                    const additionalRelationTitles = {};

                    report.items.forEach((item) => {
                        if (item.additional_relations) {
                            item.additional_relations.forEach((relationItem) => {
                                additionalRelationTitles[relationItem.relation_key] = relationItem.name;
                            });
                        }
                    });

                    this.additionalRelationTitles = Object.values(additionalRelationTitles) || [];

                    // Convert to number items collection
                    report.items.map((item) => {
                        for (let key in item) {
                            if (key in item) {
                                this.convertToNumber[key] && (item[key + '_number'] = Number(item[key].replace(/\s+/g, '')));
                                key === 'entity' && item['entity'] && (item['id'] = item.entity.id);
                            }
                        }

                        return item;
                    });

                    Object.entries(report.totals).forEach(([key, item]) => {
                        this.convertToNumber[key] && (report.totals[key + '_number'] = Number(item.replace(/\s+/g, '')));
                    });
                }

                if (report.totalsByDates) {
                    Object.values(report.totalsByDates).map((item) => {
                        for (let key in item) {
                            if (key in item) {
                                this.convertToNumber[key] && (item[key + '_number'] = Number(item[key].replace(/\s+/g, '')));
                                key === 'entity' && item['entity'] && (item['id'] = item.entity.id);
                            }
                        }

                        return item;
                    });
                }

                this.report = {...this.report, ...report};
                this.$emit('setReport', {
                    report: this.report,
                    typeReport: this.typeReport,
                    additionalRelationTitles: this.additionalRelationTitles,
                });
            } catch (error) {
                let {
                    data: { errors, message },
                } = error.response;

                if (isArray(message)) {
                    message = message[0];
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });

                this.$emit('setReport', {
                    report: {},
                });
            } finally {
                if (byDates) {
                    this.isChartsLoading = false;
                    this.$emit('setChartsLoading', this.isChartsLoading);
                } else {
                    this.isLoading = false;
                    this.$emit('setLoading', this.isLoading);
                }
            }
        },
        addGetParam(key, value) {
            let params = '';
            if (key === 'date') {
                let date = value.split(' - ');
                params += 'start=' + date[0] + '&end=' + date[1];
            } else {
                params += key + '=' + value;
            }

            return params;
        },
        clearFilter() {
            let self = this;
            self.formItems.forEach(function (value) {
                let itemRef = self.getCurrentRef(value.id);
                if (itemRef) {
                    itemRef.clear();
                    self.externalParams && self.externalParams[value.id] && (self.externalParams[value.id] = null);
                }
            });
            self.itemsFilterSelected = [];
            self.existGroupBy = false;
            self.existFilter = false;
            self.report = {};
            self.$emit('setReport', {
                report: self.report,
                typeReport: self.typeReport,
                additionalRelationTitles: self.additionalRelationTitles,
            });
            self.changeFilterParameter();
        },
        changeDate(data) {
            this.checkExistGroupBy({ date: data });
            this.getCurrentRef('date').setValue(data);
            this.changeFilterParameter();
        },
        changeGroupBy(data) {
            this.checkExistGroupBy({ groupBy: data });
            this.changeFilterParameter();
        },
        changeClickerFilter() {
            this.changeFilterParameter();
        },
        changeWithoutClickServer() {
            this.changeFilterParameter();
        },
        checkExistGroupBy(data) {
            let datesExist = false;
            let groupExist = false;
            if (data.date) {
                datesExist = data.date && data.date.length > 0;
                let groupByRef = this.getCurrentRef('groupBy');
                groupExist = groupByRef.getValue() && groupByRef.getValue().length > 0;
            } else if (data.groupBy) {
                groupExist = data.groupBy && data.groupBy.length > 0;
                let dateRef = this.getCurrentRef('date');
                datesExist = dateRef.getValue() && dateRef.getValue().length > 0;
            }
            this.existGroupBy = datesExist && groupExist;
        },
        changeOnlyActive(data) {
            this.onlyActive = data;
            this.filterOnlyActive();
            this.changeFilterParameter();
        },
        filterOnlyActive() {
            if (this.$refs['formItemsDynamicRef']) {
                this.$refs['formItemsDynamicRef'].forEach((value) => {
                    let currentItems = this.dataItems[value.id] || [];
                    if (this.onlyActive) {
                        // Filter items
                        value.itemsData = currentItems.filter((el) => el.active === undefined || el.active === true);

                        // Filter selected items
                        let itemValues = value.getValue();
                        let filteredValue =
                            itemValues && value.itemsData
                                ? value.itemsData.filter((value) => itemValues.indexOf(value.id) !== -1).map((value) => value.id)
                                : null;

                        // Set selected items
                        value.setModelAndValue(filteredValue);
                    } else {
                        value.itemsData = currentItems;
                    }
                });
            }
        },
        changeFilter(data) {
            this.itemsFilterSelected = this.itemsFilter.filter((value) => data.indexOf(value.id) !== -1);
            this.existFilter = data && data.length > 0;
            this.cleanDynamicItems();
            this.filterOnlyActive();
            this.filteringApis();
            this.changeFilterParameter();
        },
        changeFilterProvider() {
            this.filteringApis();
            this.changeFilterParameter();
        },
        changeFilterDefault() {
            this.changeFilterParameter();
        },
        filteringApis() {
            let providersRef = this.getCurrentRef('provider');
            let providerSelected =
                providersRef && providersRef.getValue() && providersRef.getValue().length > 0 ? providersRef.getValue() : null;

            let apisRef = this.getCurrentRef('api');
            if (apisRef) {
                if (providerSelected) {
                    providerSelected = providerSelected.map((value) => {
                        return Number(value);
                    });

                    if (this.dataItems['api']) {
                        // Data items for multiselect options
                        apisRef.itemsData = this.dataItems['api'].filter((value) => providerSelected.indexOf(value.provider_id) !== -1);

                        // Set model and value for multiselect
                        let apiValue = apisRef.getValue();
                        let apiFilteredValue =
                            apiValue && apisRef.itemsData
                                ? apisRef.itemsData.filter((value) => apiValue.indexOf(value.id) !== -1).map((value) => value.id)
                                : null;
                        apisRef.setModelAndValue(apiFilteredValue);
                    }
                } else {
                    apisRef.itemsData = this.dataItems['api'] || [];
                }
            }
        },
        getItems(id) {
            return this.dataItems[id];
        },
        setItems(data, id) {
            this.$emit('setMainItems', data, id);

            if (id === 'api') {
                this.filteringApis();
            }

            this.checkDynamicFilters();
        },
        checkDynamicFilters() {
            let allDataLoaded = true;

            this.itemsFilterSelected.forEach((item) => {
                if (!this.dataItems[item.id]) {
                    allDataLoaded = false;
                }
            });

            this.filterOnlyActive();
            this.$emit('setDisabledDynamicFilter', !allDataLoaded);
        },
        getCurrentRef(key) {
            let currentRef = null;
            let currentItem = this.formItems.find((currentValue) => {
                return key === currentValue.id;
            });

            if (currentItem.type === 'static') {
                let keyString = key + 'Ref';
                currentRef = this.$refs[keyString] ? this.$refs[keyString] : null;
            } else {
                let keyString = 'formItemsDynamicRef';
                currentRef =
                    this.$refs[keyString] && this.$refs[keyString].length > 0 ? this.getDynamicRef(this.$refs[keyString], key) : null;
            }

            return currentRef;
        },
        getDynamicRef(refs, key) {
            let currentRef = refs.find((currentValue) => {
                return key === currentValue.id;
            });

            return currentRef ? currentRef : null;
        },
        cleanDynamicItems() {
            this.formItems.forEach((value) => {
                if (value.type === 'dynamic') {
                    let currentRef = this.getCurrentRef(value.id);
                    let existFilter = this.itemsFilterSelected.filter((valueSelect) => valueSelect.id === value.id).length > 0;

                    if (currentRef && currentRef.clear && !existFilter) {
                        currentRef.clear();
                    }
                }
            });
        },
        getDynamicId(id) {
            return this.idTab + '_' + id;
        },
        changeFilterParameter() {
            this.$emit('changeFilterParameter');
        },
        changeAdditionalFieldsVisibility() {
            this.isAdditionalFieldsHidden = !this.isAdditionalFieldsHidden;
        },
        callMethod(method) {
            this[method]();
        },
    },
};
</script>
